/**
 * UI Profile System
 *
 * Three visual profiles that control tile appearance across the entire UI.
 * Each profile maps to a tile concept ID from dashboard-tile-concepts.ts
 * and defines CSS variable overrides for accent colors, borders, fonts, etc.
 *
 * Extensible: add more style tokens here as the UI grows.
 */

export const UI_PROFILES = ['classic', 'scifi', 'baseball'] as const
export type UIProfile = typeof UI_PROFILES[number]

export function isValidProfile(p: unknown): p is UIProfile {
  return typeof p === 'string' && (UI_PROFILES as readonly string[]).includes(p)
}

export const DEFAULT_PROFILE: UIProfile = 'classic'

export interface ProfileDefinition {
  id: UIProfile
  label: string
  icon: string
  /** Short description shown in tooltip */
  description: string
  /** Tile concept ID from dashboard-tile-concepts.ts; null = classic plain tiles */
  tileConcept: string | null
  /** CSS variable overrides applied via data-profile attribute */
  cssOverrides: {
    dark: Record<string, string>
    light: Record<string, string>
  }
}

export const PROFILE_DEFINITIONS: Record<UIProfile, ProfileDefinition> = {
  classic: {
    id: 'classic',
    label: 'Classic',
    icon: '🎨',
    description: 'Clean and simple — the original look',
    tileConcept: null,
    cssOverrides: {
      dark: {
        // Classic uses the default CSS vars — no overrides needed
      },
      light: {},
    },
  },
  scifi: {
    id: 'scifi',
    label: 'Sci-Fi Hub',
    icon: '🔮',
    description: 'Neon glow, frosted glass, cyber aesthetic',
    tileConcept: 'neon-glass',
    cssOverrides: {
      dark: {
        '--t-accent':       '#facc15',
        '--t-accent-text':  '#000000',
        '--t-accent-dim':   '#1a1708',
        '--t-border':       '#0a2a2f',
        '--t-border-2':     '#0d3d45',
        '--t-border-hover': '#facc1544',
        '--t-surface':      '#0a1418',
        '--t-surface-sub':  '#080f12',
        '--t-header':       '#060d10',
      },
      light: {
        '--t-accent':       '#b45309',
        '--t-accent-text':  '#ffffff',
        '--t-accent-dim':   '#fef3c7',
      },
    },
  },
  baseball: {
    id: 'baseball',
    label: 'Baseball Card',
    icon: '⚾',
    description: 'Collectible card with gold trim and holographic shimmer',
    tileConcept: 'sports-card',
    cssOverrides: {
      dark: {
        '--t-accent':       '#f59e0b',
        '--t-accent-text':  '#000000',
        '--t-accent-dim':   '#1f1508',
        '--t-border':       '#2a2015',
        '--t-border-2':     '#3d2e1a',
        '--t-border-hover': '#f59e0b55',
        '--t-surface':      '#151210',
        '--t-surface-sub':  '#100e0c',
        '--t-header':       '#0d0b09',
      },
      light: {
        '--t-accent':       '#b45309',
        '--t-accent-text':  '#ffffff',
        '--t-accent-dim':   '#fef3c7',
      },
    },
  },
}
